import { useState } from 'react'
import { GitBranch, AlertTriangle, Clock, Loader2, FileText, Play, CheckCircle, XCircle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useR2List, useR2Read } from '@/hooks/useApi'
import { getToken } from '@/lib/auth'

type DriftWorker = {
  name: string
  drift_detected: boolean | null
  drift_detail?: string
  recommendation?: string
  checks?: Record<string, unknown>
}

type DriftCheckResult = {
  checked: number
  drifted_count: number
  clean: boolean
  summary: string
  workers: DriftWorker[]
}

export function DriftPanel() {
  const { data, isLoading, refetch } = useR2List('drift/', 200)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const { data: detail, isLoading: detailLoading } = useR2Read(selectedKey)
  const [checking, setChecking] = useState(false)
  const [liveResult, setLiveResult] = useState<DriftCheckResult | null>(null)
  const [checkError, setCheckError] = useState<string | null>(null)

  const driftEntries = data?.objects || []

  async function runDriftCheck() {
    setChecking(true)
    setLiveResult(null)
    setCheckError(null)
    try {
      const res = await fetch('/api/dashboard/drift/check', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || json.detail || 'Check failed')
      setLiveResult(json)
      refetch()
    } catch (e) {
      setCheckError(e instanceof Error ? e.message : String(e))
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            Drift Viewer
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            {!isLoading && <Badge variant="muted" className="text-[10px]">{driftEntries.length} entries</Badge>}
            <button
              onClick={runDriftCheck}
              disabled={checking}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors disabled:opacity-50"
            >
              {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Run Drift Check
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {driftEntries.length === 0 && !isLoading ? (
            <div className="text-center py-8 text-sm text-text-muted">
              <GitBranch className="h-8 w-8 mx-auto mb-2 text-text-muted/50" />
              No drift entries detected
            </div>
          ) : (
            <div className="space-y-1">
              {driftEntries.map(entry => {
                const filename = entry.key.split('/').pop() || entry.key
                const isJson = filename.endsWith('.json')
                const isSelected = selectedKey === entry.key
                return (
                  <button
                    key={entry.key}
                    onClick={() => setSelectedKey(isSelected ? null : entry.key)}
                    className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors ${isSelected ? 'bg-accent/10 border border-accent/30' : 'hover:bg-accent/5 border border-transparent'}`}
                  >
                    <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{filename}</div>
                      <div className="flex items-center gap-2 text-[10px] text-text-muted">
                        <Clock className="h-3 w-3" />
                        {new Date(entry.uploaded).toLocaleDateString()}
                        <Badge variant="muted" className="text-[9px]">{isJson ? 'JSON' : 'Text'}</Badge>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {checkError && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-red-400">
              <XCircle className="h-4 w-4 shrink-0" />
              {checkError}
            </div>
          </CardContent>
        </Card>
      )}

      {liveResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              {liveResult.clean ? <CheckCircle className="h-4 w-4 text-green-400" /> : <AlertTriangle className="h-4 w-4 text-warning" />}
              Live Drift Check — {liveResult.checked} workers checked
              <Badge variant="muted" className="text-[10px] ml-auto">
                {liveResult.drifted_count} drifted
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-text-muted mb-3">{liveResult.summary}</p>
            <div className="space-y-2">
              {liveResult.workers.filter(w => w.drift_detected !== false).map(w => (
                <div key={w.name} className={`p-3 rounded-lg border text-xs space-y-1 ${w.drift_detected === true ? 'border-warning/30 bg-warning/5' : 'border-border bg-surface-elevated'}`}>
                  <div className="flex items-center gap-2">
                    {w.drift_detected === true
                      ? <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
                      : <Loader2 className="h-3.5 w-3.5 text-text-muted shrink-0" />}
                    <span className="font-mono font-medium text-text-primary">{w.name}</span>
                    <Badge variant="muted" className="text-[9px]">
                      {w.drift_detected === true ? 'drifted' : 'unverifiable'}
                    </Badge>
                  </div>
                  {w.drift_detail && <p className="text-text-secondary pl-5">{w.drift_detail}</p>}
                  {w.recommendation && <p className="text-accent pl-5">{w.recommendation}</p>}
                </div>
              ))}
              {liveResult.drifted_count === 0 && (
                <div className="text-center py-4 text-sm text-green-400 flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4" /> All workers match repo HEAD
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedKey && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" />
              <span className="truncate font-mono text-xs">{selectedKey}</span>
              {detailLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {detail ? (
              <pre className="text-xs text-text-secondary bg-background rounded-lg p-4 overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                {typeof detail.data === 'string' ? detail.data : JSON.stringify(detail.data, null, 2)}
              </pre>
            ) : detailLoading ? (
              <div className="py-4 text-center text-sm text-text-muted">Loading...</div>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
